import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, MapPin, CalendarClock, RefreshCw, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TeamFlag } from "./TeamFlag";
import { formatKickoff } from "@/lib/utils-app";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface MatchRow {
  id: string;
  phase: string;
  group_name: string | null;
  kickoff_at: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  status: string;
  match_number: number | null;
  stadium: string | null;
  city: string | null;
  source_url: string | null;
  tournament_name: string | null;
}

interface GoalRow {
  id: string;
  side: string;
  player_name: string;
  minute: number | null;
  goal_type: string | null;
}

export function useMatchDetail(id: string | undefined, enabled = true) {
  const [match, setMatch] = useState<MatchRow | null>(null);
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id || !enabled) return;
    let active = true;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("matches")
        .select(
          "id, phase, group_name, kickoff_at, home_team, away_team, home_score, away_score, status, match_number, stadium, city, source_url, tournament_name",
        )
        .eq("id", id)
        .maybeSingle();
      if (!active) return;
      setMatch((data as MatchRow) ?? null);
      setLoading(false);
      if (data) {
        const { data: g } = await supabase
          .from("match_goals")
          .select("id, side, player_name, minute, goal_type")
          .eq("match_id", id)
          .order("minute", { ascending: true });
        if (active) setGoals((g as GoalRow[]) ?? []);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, enabled, reloadKey]);

  return { match, goals, loading, refresh: () => setReloadKey((k) => k + 1) };
}

export function MatchDetailContent({
  match,
  goals,
  loading,
  onSynced,
}: {
  match: MatchRow | null;
  goals: GoalRow[];
  loading: boolean;
  onSynced?: () => void;
}) {
  const { t } = useTranslation();
  const { isSuperAdmin } = useAuth();
  const [syncing, setSyncing] = useState(false);

  const syncNow = async () => {
    if (!match) return;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-match-results", {
        body: { matchId: match.id },
      });
      if (error) throw error;
      const updated = (data as { updated?: number } | null)?.updated ?? 0;
      toast.success(
        updated > 0
          ? t("league.syncDone", "Výsledek byl aktualizován z UEFA.")
          : t("league.syncNothing", "UEFA zatím nemá konečný výsledek."),
      );
      if (updated > 0) onSynced?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  };
  const finished = match && match.home_score !== null && match.away_score !== null;
  const venue = match ? [match.stadium, match.city].filter(Boolean).join(", ") : "";

  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!match) return <p className="text-muted-foreground">{t("league.notFound")}</p>;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {match.tournament_name && <Badge variant="secondary">{match.tournament_name}</Badge>}
            <Badge variant="outline">{t(`phases.${match.phase}`, match.phase)}</Badge>
            {match.group_name && <Badge variant="outline">{match.group_name}</Badge>}
            {match.match_number != null && (
              <span className="text-muted-foreground">#{match.match_number}</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
              <span className="font-semibold truncate">{match.home_team}</span>
              <TeamFlag team={match.home_team} size="md" />
            </div>
            <div className="min-w-[84px] text-center">
              {finished ? (
                <span className="text-2xl font-bold tabular-nums">
                  {match.home_score}:{match.away_score}
                </span>
              ) : (
                <span className="text-lg font-bold tabular-nums whitespace-nowrap">
                  {new Date(match.kickoff_at).toLocaleTimeString("cs-CZ", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
            <div className="flex-1 flex items-center gap-2 min-w-0">
              <TeamFlag team={match.away_team} size="md" />
              <span className="font-semibold truncate">{match.away_team}</span>
            </div>
          </div>

          <div className="space-y-1 text-sm text-muted-foreground">
            <p className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 shrink-0" />
              {formatKickoff(match.kickoff_at)}
            </p>
            {venue && (
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0" />
                {venue}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {match.source_url && (
              <Button asChild>
                <a href={match.source_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  {t("league.openOfficial", "Otevřít oficiální stránku zápasu")}
                </a>
              </Button>
            )}
            {isSuperAdmin && match.source_url && (
              <Button variant="outline" onClick={syncNow} disabled={syncing}>
                {syncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {t("league.syncFromUefa", "Načíst výsledek z UEFA")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {goals.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <h2 className="text-sm font-semibold">{t("league.goals", "Góly")}</h2>
            <ul className="space-y-1 text-sm">
              {goals.map((g) => (
                <li key={g.id} className="flex items-center gap-2">
                  <span className="w-10 shrink-0 tabular-nums text-muted-foreground">
                    {g.minute != null ? `${g.minute}'` : ""}
                  </span>
                  <span className="font-medium">{g.player_name}</span>
                  {g.goal_type && g.goal_type !== "SCORED" && (
                    <Badge variant="outline" className="text-[10px]">
                      {g.goal_type === "PENALTY"
                        ? t("league.goalPenalty", "penalta")
                        : g.goal_type === "OWN"
                          ? t("league.goalOwn", "vlastní")
                          : g.goal_type.toLowerCase()}
                    </Badge>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {g.side === "home" ? match.home_team : match.away_team}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {match.source_url && (
        <p className="text-xs text-muted-foreground">
          {t(
            "league.embedBlocked",
            "Oficiální stránka zápasu nepovoluje vložení do jiného webu, otevře se proto v nové záložce.",
          )}
        </p>
      )}
    </div>
  );
}

export function MatchDetailDialog({
  matchId,
  open,
  onOpenChange,
}: {
  matchId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const { match, goals, loading, refresh } = useMatchDetail(matchId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("league.matchDetails")}</DialogTitle>
        </DialogHeader>
        <MatchDetailContent match={match} goals={goals} loading={loading} onSynced={refresh} />
      </DialogContent>
    </Dialog>
  );
}

export function MatchDetailsLink({ matchId, className }: { matchId: string; className?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          "inline-flex items-center gap-1 text-primary hover:underline whitespace-nowrap"
        }
      >
        <ExternalLink className="h-3 w-3 shrink-0" />
        {t("league.matchDetails")}
      </button>
      {open && <MatchDetailDialog matchId={matchId} open={open} onOpenChange={setOpen} />}
    </>
  );
}
